import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── GLOBAL STYLES ──
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'DM Sans', sans-serif;
    background: #f8f9fa;
    color: #1a1a2e;
    -webkit-font-smoothing: antialiased;
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeImg {
    from { opacity: 0; transform: scale(1.03); }
    to   { opacity: 1; transform: scale(1); }
  }
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.65); }
  }
`;

// ── HERO SLIDES — muted, neutral, sophisticated palette ──
const heroSlides = [
  {
    badge: "🇮🇳 India's #1 Energy Market e-HUB",
    title: "Physical · Digital ·",
    titleAccent: "Service Partner Network",
    desc: "Source cables, chargers, inverters, solar panels and IoT devices. Manage via digital platforms. Connect with EPC partners, electricians, workshops and repair centres — all in one ecosystem.",
    accent: "#2d6a4f",
    accentLight: "#eaf4ef",
    accentMid: "#c2ddd0",
    bg: "#f5faf7",
    btn1: { label: "Browse Products", to: "/products" },
    btn2: { label: "Get Consultation", to: "/contact" },
  },
  {
    badge: "⚡ EV Charging Solutions",
    title: "AC & DC Chargers,",
    titleAccent: "EV Cables & Connectors",
    desc: "India's largest B2B marketplace for EV charging infrastructure. AC chargers, DC fast chargers, battery swapping stations — sourced directly from certified vendors.",
    accent: "#1e4d8c",
    accentLight: "#eaf0fb",
    accentMid: "#bccfe9",
    bg: "#f4f8fd",
    btn1: { label: "Browse EV Products", to: "/products" },
    btn2: { label: "Talk to Expert", to: "/contact" },
  },
  {
    badge: "☀️ Solar Energy",
    title: "Solar Panels, Pumps,",
    titleAccent: "Inverters & Complete Kits",
    desc: "Mono PERC, bifacial panels, solar pumps, hybrid inverters and full solar kits. Best prices from verified solar vendors across India.",
    accent: "#8a5a0f",
    accentLight: "#faf3e5",
    accentMid: "#e6cfa0",
    bg: "#fdf8f0",
    btn1: { label: "Browse Solar Products", to: "/products" },
    btn2: { label: "Solar Consultation", to: "/contact" },
  },
  {
    badge: "🔋 Energy Storage",
    title: "Lithium Batteries,",
    titleAccent: "BMS & Storage Systems",
    desc: "Lithium-ion, lead acid, energy storage systems and BMS units for residential, commercial and industrial use across India.",
    accent: "#4a3580",
    accentLight: "#f0ecfb",
    accentMid: "#cdc3e8",
    bg: "#f7f5fd",
    btn1: { label: "Browse Storage", to: "/products" },
    btn2: { label: "Get a Quote", to: "/contact" },
  },
  {
    badge: "📡 IoT & Digital Platforms",
    title: "Smart Gateways,",
    titleAccent: "Energy Meters & Software",
    desc: "Real-time monitoring, asset management, charger management software, energy billing — all integrated in one AI-powered digital ecosystem.",
    accent: "#0e6680",
    accentLight: "#e6f5f9",
    accentMid: "#aad4e3",
    bg: "#f3f9fc",
    btn1: { label: "Explore Digital", to: "/platform" },
    btn2: { label: "Book Demo", to: "/contact" },
  },
];

const stats = [
  { value: "500+", label: "Vendors" },
  { value: "48+", label: "Cities" },
  { value: "10K+", label: "Products" },
  { value: "₹0", label: "Commission" },
];

// ── HERO SLIDER ──
function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = (indexOrUpdater) => {
    setAnimating(true);
    setTimeout(() => {
      setCurrent((prev) =>
        typeof indexOrUpdater === "function" ? indexOrUpdater(prev) : indexOrUpdater
      );
      setAnimating(false);
    }, 280);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((prev) => (prev + 1) % heroSlides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const slide = heroSlides[current];

  return (
    <div style={{
      position: "relative",
      background: slide.bg,
      overflow: "hidden",
      borderBottom: `2px solid ${slide.accentMid}`,
      transition: "background 0.5s ease, border-color 0.5s ease",
    }}>
      {/* Soft dot-grid texture */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `radial-gradient(circle, ${slide.accentMid} 1px, transparent 1px)`,
        backgroundSize: "30px 30px",
        opacity: 0.3,
        pointerEvents: "none",
        transition: "opacity 0.5s ease",
      }} />
      {/* Soft glow right side */}
      <div style={{
        position: "absolute", top: 0, right: 0, bottom: 0, width: "50%",
        background: `radial-gradient(ellipse at 85% 50%, ${slide.accentLight} 0%, transparent 70%)`,
        pointerEvents: "none",
        transition: "all 0.5s ease",
      }} />
      {/* Top thin stripe */}
      <div style={{ height: "3px", background: `linear-gradient(90deg, ${slide.accent} 0%, ${slide.accentMid} 100%)`, position: "relative", zIndex: 1 }} />

      <div style={{
        maxWidth: "1280px", margin: "0 auto",
        padding: "60px 64px 52px",
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        gap: "48px", flexWrap: "wrap",
        position: "relative", zIndex: 1,
      }}>
        {/* LEFT TEXT */}
        <div style={{
          maxWidth: "560px",
          opacity: animating ? 0 : 1,
          transform: animating ? "translateY(14px)" : "translateY(0)",
          transition: "opacity 0.28s ease, transform 0.28s ease",
        }}>
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "8px",
            background: "white",
            color: slide.accent,
            borderRadius: "8px", padding: "7px 16px",
            fontSize: "12px", fontWeight: "700",
            fontFamily: "'Sora', sans-serif",
            letterSpacing: "0.2px",
            marginBottom: "24px",
            border: `1.5px solid ${slide.accentMid}`,
            boxShadow: `0 2px 10px ${slide.accentMid}50`,
          }}>
            <span style={{
              width: "6px", height: "6px", borderRadius: "50%",
              background: slide.accent,
              animation: "pulse-dot 2s ease-in-out infinite",
              display: "inline-block", flexShrink: 0,
            }} />
            {slide.badge}
          </div>

          <h1 style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "clamp(30px, 3.8vw, 52px)",
            fontWeight: "900",
            color: "#1a1a2e",
            lineHeight: "1.07",
            margin: "0 0 20px",
            letterSpacing: "-1.5px",
          }}>
            {slide.title}<br />
            <span style={{ color: slide.accent }}>{slide.titleAccent}</span>
          </h1>

          <p style={{
            color: "#5a6370",
            fontSize: "15.5px",
            lineHeight: "1.85",
            fontFamily: "'DM Sans', sans-serif",
            margin: "0 0 36px",
            maxWidth: "460px",
          }}>
            {slide.desc}
          </p>

          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <Link to={slide.btn1.to}>
              <button style={{
                background: slide.accent,
                color: "white",
                border: "none", borderRadius: "10px",
                padding: "14px 32px",
                fontWeight: "700", fontSize: "14px",
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                boxShadow: `0 6px 20px ${slide.accentMid}`,
                transition: "all 0.18s ease",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 28px ${slide.accentMid}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = `0 6px 20px ${slide.accentMid}`; }}>
                {slide.btn1.label}
              </button>
            </Link>
            <Link to={slide.btn2.to}>
              <button style={{
                background: "white",
                color: slide.accent,
                border: `1.5px solid ${slide.accentMid}`,
                borderRadius: "10px",
                padding: "14px 32px",
                fontWeight: "700", fontSize: "14px",
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
                transition: "all 0.18s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = slide.accent; e.currentTarget.style.background = slide.accentLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = slide.accentMid; e.currentTarget.style.background = "white"; }}>
                {slide.btn2.label}
              </button>
            </Link>
          </div>
        </div>

        {/* RIGHT STATS */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px",
          opacity: animating ? 0 : 1,
          transition: "opacity 0.28s ease",
        }}>
          {stats.map((s, i) => (
            <div key={i} style={{
              background: i % 2 === 0 ? "white" : slide.accentLight,
              borderRadius: "14px", padding: "22px 30px",
              textAlign: "center",
              border: `1.5px solid ${i % 2 === 0 ? "#e8eaed" : slide.accentMid}`,
              minWidth: "128px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
            }}>
              <div style={{
                fontSize: "26px", fontWeight: "900",
                color: i % 2 === 0 ? "#1a1a2e" : slide.accent,
                fontFamily: "'Sora', sans-serif",
                letterSpacing: "-1px",
              }}>{s.value}</div>
              <div style={{
                fontSize: "11px", color: "#8a9099",
                fontFamily: "'DM Sans', sans-serif",
                marginTop: "5px", fontWeight: "600",
                textTransform: "uppercase", letterSpacing: "0.6px",
              }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* DOT NAV */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: "10px", paddingBottom: "32px",
        position: "relative", zIndex: 1,
      }}>
        <button onClick={() => goTo((current - 1 + heroSlides.length) % heroSlides.length)}
          style={{
            background: "white", border: `1px solid ${slide.accentMid}`,
            borderRadius: "50%", width: "32px", height: "32px",
            color: slide.accent, cursor: "pointer", fontSize: "16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            transition: "all 0.18s ease",
          }}>‹</button>
        {heroSlides.map((_, i) => (
          <div key={i} onClick={() => goTo(i)} style={{
            width: i === current ? "24px" : "7px",
            height: "7px", borderRadius: "4px",
            background: i === current ? slide.accent : slide.accentMid,
            cursor: "pointer",
            transition: "all 0.3s ease",
          }} />
        ))}
        <button onClick={() => goTo((current + 1) % heroSlides.length)}
          style={{
            background: "white", border: `1px solid ${slide.accentMid}`,
            borderRadius: "50%", width: "32px", height: "32px",
            color: slide.accent, cursor: "pointer", fontSize: "16px",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            transition: "all 0.18s ease",
          }}>›</button>
      </div>
    </div>
  );
}

// ── SECTION DATA ──
const sections = [
  {
    id: "physical",
    badge: "Physical",
    badgeColor: "#2d6a4f",
    badgeBg: "#eaf4ef",
    badgeBorder: "#c2ddd0",
    heading: "What is Physical?",
    description: "Physical products form the foundation of the EcoConnect ecosystem — everything you can touch, install and operate. We source and supply certified hardware for EV, Solar, IoT and energy infrastructure directly from verified Indian manufacturers.",
    link: "/products",
    linkLabel: "Browse Physical Products",
    images: [
      { url: "https://images.unsplash.com/photo-1594818379496-da1e345b0ded?w=700&q=80", alt: "EV Charger" },
      { url: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=700&q=80", alt: "Solar Panels" },
      { url: "https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=700&q=80", alt: "Battery Storage" },
    ],
  },
  {
    id: "digital",
    badge: "Digital",
    badgeColor: "#1e4d8c",
    badgeBg: "#eaf0fb",
    badgeBorder: "#bccfe9",
    heading: "What is Digital?",
    description: "Digital is the intelligence layer of EcoConnect — software platforms that help you manage assets, control chargers, handle electricity billing and monitor energy in real time. Built for EV operators, solar plant owners, MSMEs and smart city projects.",
    link: "/platform",
    linkLabel: "Explore Digital Platforms",
    images: [
      { url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80", alt: "Dashboard Analytics" },
      { url: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=700&q=80", alt: "IoT Circuit" },
      { url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80", alt: "Data Platform" },
    ],
  },
  {
    id: "service",
    badge: "Service Partner Network",
    badgeColor: "#4a3580",
    badgeBg: "#f0ecfb",
    badgeBorder: "#cdc3e8",
    heading: "What is Service Partner Network?",
    description: "EcoConnect One Partner — a pan-India verified network of professionals who install, maintain, repair and operate EV and solar energy systems. Every partner is trained, verified and rated on our platform.",
    link: "/services",
    linkLabel: "Find Service Partners",
    images: [
      { url: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=700&q=80", alt: "Electrician at work" },
      { url: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=700&q=80", alt: "Solar Installation" },
      { url: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=700&q=80", alt: "EV Service" },
    ],
  },
  {
    id: "execution",
    badge: "Execution",
    badgeColor: "#8a5a0f",
    badgeBg: "#faf3e5",
    badgeBorder: "#e6cfa0",
    heading: "What is Execution?",
    description: "Execution is EcoConnect's end-to-end project delivery capability — from feasibility study and site assessment to full installation, commissioning and ongoing maintenance. Your energy project, from plan to live, without friction.",
    link: "/contact",
    linkLabel: "Start Your Project",
    images: [
      { url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=700&q=80", alt: "Construction Site" },
      { url: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=700&q=80", alt: "Solar Installation Work" },
      { url: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=700&q=80", alt: "Project Consulting" },
    ],
  },
];

// ── IMAGE ROTATOR ──
function ImageRotator({ images, color, borderColor }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % images.length), 3400);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div style={{ position: "relative" }}>
      <div style={{
        borderRadius: "18px", overflow: "hidden",
        boxShadow: `0 16px 48px ${borderColor}60, 0 4px 12px rgba(0,0,0,0.05)`,
        aspectRatio: "4/3", position: "relative",
        border: `1.5px solid ${borderColor}`,
      }}>
        <img
          key={active}
          src={images[active].url}
          alt={images[active].alt}
          style={{ width: "100%", height: "100%", objectFit: "cover", animation: "fadeImg 0.45s ease" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(to top, ${color}0d, transparent 55%)`,
        }} />
        <div style={{
          position: "absolute", top: "14px", right: "14px",
          background: "rgba(255,255,255,0.93)",
          backdropFilter: "blur(8px)",
          borderRadius: "7px", padding: "4px 11px",
          fontSize: "11px", fontWeight: "700",
          color: color, fontFamily: "'Sora',sans-serif",
          border: `1px solid ${borderColor}`,
        }}>
          {active + 1} / {images.length}
        </div>
      </div>
      <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
        {images.map((img, i) => (
          <div key={i} onClick={() => setActive(i)} style={{
            flex: 1, height: "64px", borderRadius: "10px",
            overflow: "hidden", cursor: "pointer",
            border: `2px solid ${i === active ? color : "transparent"}`,
            outline: i === active ? `3px solid ${borderColor}` : "none",
            transition: "all 0.2s ease",
            opacity: i === active ? 1 : 0.5,
          }}>
            <img src={img.url} alt={img.alt}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => e.target.style.display = "none"} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── INFO SECTION ──
function InfoSection({ section, reverse }) {
  return (
    <section style={{
      padding: "88px 0",
      background: reverse ? "#fafbfc" : "#ffffff",
      borderBottom: "1px solid #edeef0",
    }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 56px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "80px",
          alignItems: "center",
        }}>
          <div style={{ order: reverse ? 2 : 1 }}>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "8px",
              background: section.badgeBg,
              color: section.badgeColor,
              fontSize: "11px", fontWeight: "800",
              padding: "6px 16px", borderRadius: "7px",
              fontFamily: "'Sora', sans-serif",
              letterSpacing: "0.9px",
              textTransform: "uppercase",
              marginBottom: "26px",
              border: `1.5px solid ${section.badgeBorder}`,
            }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: section.badgeColor, flexShrink: 0 }} />
              {section.badge}
            </div>

            <h2 style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: "clamp(24px, 2.8vw, 38px)",
              fontWeight: "900",
              color: "#1a1a2e",
              margin: "0 0 18px",
              lineHeight: "1.1",
              letterSpacing: "-0.8px",
            }}>
              {section.heading}
            </h2>

            <p style={{
              fontSize: "15.5px",
              color: "#5a6370",
              lineHeight: "1.88",
              fontFamily: "'DM Sans', sans-serif",
              margin: "0 0 36px",
            }}>
              {section.description}
            </p>

            <Link to={section.link} style={{ textDecoration: "none" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "10px",
                background: section.badgeBg,
                color: section.badgeColor,
                fontWeight: "700", fontSize: "14px",
                fontFamily: "'Sora', sans-serif",
                padding: "12px 22px",
                borderRadius: "9px",
                border: `1.5px solid ${section.badgeBorder}`,
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = section.badgeColor;
                  e.currentTarget.style.color = "white";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 8px 22px ${section.badgeBorder}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = section.badgeBg;
                  e.currentTarget.style.color = section.badgeColor;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                {section.linkLabel}
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </Link>
          </div>

          <div style={{ order: reverse ? 1 : 2 }}>
            <ImageRotator images={section.images} color={section.badgeColor} borderColor={section.badgeBorder} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── SECTION INTRO STRIP ──
function SectionIntroStrip() {
  return (
    <div style={{
      background: "#f8fafc",
      borderBottom: "1px solid #e8eaed",
      padding: "52px 56px",
    }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
        <p style={{
          fontSize: "11px", fontWeight: "800",
          color: "#aab0b8", letterSpacing: "2.5px",
          textTransform: "uppercase",
          fontFamily: "'Sora', sans-serif",
          marginBottom: "14px",
        }}>
          OUR ECOSYSTEM
        </p>
        <h2 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(20px, 2.4vw, 32px)",
          fontWeight: "900",
          color: "#1a1a2e",
          letterSpacing: "-0.5px",
          lineHeight: "1.18",
          maxWidth: "580px",
          margin: "0 auto",
        }}>
          Everything India's Energy Market Needs — In One Place
        </h2>
      </div>
    </div>
  );
}

// ── MAIN HOME ──
function Home() {
// ════════════════════════════════════════════════
// UPDATED CollaborationsMarquee — replace karo
// ════════════════════════════════════════════════

const partnerLogos = [
  { name: "Ajeevi Technologies", display: "ajeevi",         weight: "800", spacing: "3px",    size: "13px" },
  { name: "Thunder EV",          display: "Thunder",        weight: "900", spacing: "-0.5px", size: "20px" },
  { name: "YoCharge",            display: "YoCharge",       weight: "400", spacing: "0px",    size: "19px" },
  { name: "E-Fill Electric",     display: "E-Fill Electric",weight: "700", spacing: "0.5px",  size: "14px" },
  { name: "EV Dock",             display: "EV DOCK®",       weight: "900", spacing: "2px",    size: "17px" },
  { name: "EVee Buddy",          display: "EVee Buddy",     weight: "400", spacing: "0px",    size: "17px" },
  { name: "PlugzMart",           display: "PLUGZMART",      weight: "900", spacing: "3px",    size: "14px" },
  { name: "Verde Mobility",      display: "VerdeMobility",  weight: "300", spacing: "-0.5px", size: "18px" },
  { name: "Waaree Energies",     display: "Waaree",         weight: "700", spacing: "1px",    size: "20px" },
  { name: "Luminous Power",      display: "Luminous",       weight: "300", spacing: "2px",    size: "18px" },
];

function CollaborationsMarquee() {
  const doubled = [...partnerLogos, ...partnerLogos];

  return (
    <section style={{
      background: "#ffffff",
      borderTop: "1px solid #edeef0",
      borderBottom: "1px solid #edeef0",
      padding: "52px 0",
      overflow: "hidden",
    }}>

      {/* HEADING */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "center", gap: "24px",
        marginBottom: "40px", padding: "0 56px",
      }}>
        <div style={{ flex: 1, height: "1px", background: "linear-gradient(to right, transparent, #dde1e7)" }} />

        <div style={{ textAlign: "center" }}>
          <p style={{
            fontSize: "18px",
            fontWeight: "700",
            color: "#1a1a2e",
            letterSpacing: "-0.3px",
            fontFamily: "'DM Sans', sans-serif",
            margin: 0,
            lineHeight: 1,
          }}>
            Trusted Partners
            <span style={{
              color: "#2d6a4f",
              marginLeft: "8px",
              marginRight: "8px",
            }}>&</span>
            Collaborations
          </p>
          <p style={{
            fontSize: "11px",
            fontWeight: "500",
            color: "#aab0b8",
            letterSpacing: "1.5px",
            fontFamily: "'Sora', sans-serif",
            textTransform: "uppercase",
            margin: "6px 0 0",
          }}>
            India's Energy Ecosystem Network
          </p>
        </div>

        <div style={{ flex: 1, height: "1px", background: "linear-gradient(to left, transparent, #dde1e7)" }} />
      </div>

      {/* MARQUEE TRACK */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: "140px",
          background: "linear-gradient(to right, #ffffff, transparent)",
          zIndex: 2, pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: "140px",
          background: "linear-gradient(to left, #ffffff, transparent)",
          zIndex: 2, pointerEvents: "none",
        }} />

        <div style={{
          display: "flex",
          alignItems: "center",
          animation: "marqueeScroll 32s linear infinite",
          width: "max-content",
        }}>
          {doubled.map((p, i) => (
            <div key={i} style={{
              padding: "0 52px",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              borderRight: "1px solid #edeef0",
            }}>
              <span
                style={{
                  fontFamily: (p.weight === "300" || p.weight === "400")
                    ? "'DM Sans', sans-serif"
                    : "'Sora', sans-serif",
                  fontSize: p.size,
                  fontWeight: p.weight,
                  color: "#8a9099",
                  letterSpacing: p.spacing,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  transition: "color 0.25s ease",
                  cursor: "default",
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = "#374151"}
                onMouseLeave={(e) => e.currentTarget.style.color = "#8a9099"}
              >
                {p.display}
              </span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes marqueeScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
  return (
    <main style={{ background: "#ffffff", minHeight: "100vh" }}>
      <style>{globalStyles}</style>

      <HeroSlider />

      {/* THIN RAINBOW DIVIDER */}
      <div style={{ height: "3px", background: "linear-gradient(90deg, #2d6a4f 0%, #1e4d8c 25%, #4a3580 50%, #8a5a0f 75%, #0e6680 100%)" }} />

      {/* SECTION INTRO */}
      <SectionIntroStrip />

      {/* 4 INFO SECTIONS */}
      {sections.map((section, i) => (
        <InfoSection key={section.id} section={section} reverse={i % 2 !== 0} />
      ))}

      <CollaborationsMarquee />

      {/* BOTTOM CTA */}
      <div style={{
        background: "#1a1a2e",
        padding: "88px 56px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "-100px", right: "8%",
          width: "360px", height: "360px", borderRadius: "50%",
          background: "rgba(45,106,79,0.07)", pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: "-80px", left: "6%",
          width: "280px", height: "280px", borderRadius: "50%",
          background: "rgba(30,77,140,0.07)", pointerEvents: "none",
        }} />

        <div style={{
          display: "inline-flex", alignItems: "center", gap: "8px",
          background: "rgba(45,106,79,0.14)",
          border: "1px solid rgba(45,106,79,0.28)",
          borderRadius: "7px", padding: "6px 16px",
          fontSize: "11px", color: "#8ecfb0",
          fontWeight: "700", fontFamily: "'DM Sans', sans-serif",
          marginBottom: "28px", letterSpacing: "1px",
          textTransform: "uppercase",
        }}>
          🇮🇳 Made for India's Energy Transition
        </div>

        <h2 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(26px, 3vw, 42px)",
          fontWeight: "900", color: "white",
          margin: "0 0 16px", lineHeight: "1.1",
          letterSpacing: "-1px",
        }}>
          Join India's Energy Ecosystem
        </h2>

        <p style={{
          color: "rgba(255,255,255,0.45)",
          fontSize: "15.5px", lineHeight: "1.75",
          fontFamily: "'DM Sans', sans-serif",
          maxWidth: "480px", margin: "0 auto 40px",
        }}>
          Whether you're sourcing energy products or selling them — EcoConnect connects you to 500+ vendors, EPCs and service partners across 48+ cities.
        </p>

        <Link to="/contact">
          <button style={{
            background: "#2d6a4f", color: "white",
            border: "none", borderRadius: "10px",
            padding: "16px 48px", fontWeight: "800",
            fontSize: "15px", cursor: "pointer",
            fontFamily: "'Sora', sans-serif",
            boxShadow: "0 8px 24px rgba(45,106,79,0.35)",
            transition: "all 0.18s ease",
            letterSpacing: "0.2px",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 32px rgba(45,106,79,0.45)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(45,106,79,0.35)"; }}>
            Join as an Ecosystem Partner
          </button>
        </Link>
      </div>

    </main>
  );
}

export default Home;